/**
 * Kostenplaats-first verdeling canvas.
 *
 * The mirror of the label-first `BudgetSplitDesigner` canvas: here the user
 * starts from "welke werken doe ik in welke unit?". Drag a unit (kostenplaats)
 * from the top bar onto the canvas to create a group, then drag labels onto
 * that group to plan the works (one budgetlijn per label) for that single unit.
 *
 * This view is purely additive — it produces the exact same `SplitItemPayload`
 * shape as the label-first view (each allocation → one InvestmentItem scoped to
 * the group's single unit), so the existing `/split` endpoint is reused as-is.
 */

import React, { useState } from "react";
import { Coin, Cube, Tag, Trash, Wallet } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  RenovationLabel,
  InvestmentItemBudgetLabel,
} from "screens/investment/types/investment";
import { UnitDTO } from "shared/types/unit";
import {
  formatCurrency,
  getUnitSurface,
  LABEL_TYPE_TONE,
  newDraftId,
} from "./budgetSplitShared";

/** One planned budgetlijn inside a kostenplaats group. */
export interface KostenplaatsAllocation {
  allocId: string;
  labelId: number | null;
  labelName: string;
  labelType?: string;
  title: string;
  description: string;
  amount: number;
  budgetLabel: InvestmentItemBudgetLabel | null;
}

/** A unit (kostenplaats) on the canvas with the works planned for it. */
export interface KostenplaatsGroup {
  unitId: string;
  allocations: KostenplaatsAllocation[];
}

interface KostenplaatsVerdelingCanvasProps {
  groups: KostenplaatsGroup[];
  onChange: (next: KostenplaatsGroup[]) => void;
  unitsById: Map<string, UnitDTO>;
  bucketTotal: number;
  /** Drag-state accessors shared with the parent designer. */
  getDraggedUnit: () => string | null;
  getDraggedLabel: () => RenovationLabel | null;
  isAllUnitsDrag: () => boolean;
  clearDrag: () => void;
  /** All building unit ids — used to materialise the "alle units" drop. */
  allUnitIds: string[];
}

export const KostenplaatsVerdelingCanvas: React.FC<
  KostenplaatsVerdelingCanvasProps
> = ({
  groups,
  onChange,
  unitsById,
  bucketTotal,
  getDraggedUnit,
  getDraggedLabel,
  isAllUnitsDrag,
  clearDrag,
  allUnitIds,
}) => {
  const [isCanvasHovering, setIsCanvasHovering] = useState(false);

  const addUnitGroup = (unitId: string) => {
    if (groups.some((g) => g.unitId === unitId)) return;
    onChange([...groups, { unitId, allocations: [] }]);
  };

  const addAllUnitGroups = () => {
    const existing = new Set(groups.map((g) => g.unitId));
    const additions = allUnitIds
      .filter((id) => !existing.has(id))
      .map((unitId) => ({ unitId, allocations: [] as KostenplaatsAllocation[] }));
    if (additions.length === 0) return;
    onChange([...groups, ...additions]);
  };

  const removeGroup = (unitId: string) => {
    onChange(groups.filter((g) => g.unitId !== unitId));
  };

  const addAllocation = (unitId: string, label: RenovationLabel) => {
    onChange(
      groups.map((g) =>
        g.unitId === unitId
          ? {
              ...g,
              allocations: [
                ...g.allocations,
                {
                  allocId: newDraftId("a"),
                  labelId: label.id,
                  labelName: label.name,
                  labelType: label.type,
                  title: `Budget voor ${label.name}`,
                  description: "",
                  amount: 0,
                  // Bucket-derived lines are always strategic budget.
                  budgetLabel: InvestmentItemBudgetLabel.STRATEGISCH,
                },
              ],
            }
          : g,
      ),
    );
  };

  const updateAllocation = (
    unitId: string,
    allocId: string,
    patch: Partial<KostenplaatsAllocation>,
  ) => {
    onChange(
      groups.map((g) =>
        g.unitId === unitId
          ? {
              ...g,
              allocations: g.allocations.map((a) =>
                a.allocId === allocId ? { ...a, ...patch } : a,
              ),
            }
          : g,
      ),
    );
  };

  const removeAllocation = (unitId: string, allocId: string) => {
    onChange(
      groups.map((g) =>
        g.unitId === unitId
          ? {
              ...g,
              allocations: g.allocations.filter((a) => a.allocId !== allocId),
            }
          : g,
      ),
    );
  };

  // ---- Canvas-level drop (units → new groups) ----------------------------
  const canvasAccepts = () => !!getDraggedUnit() || isAllUnitsDrag();

  const handleCanvasDragOver = (e: React.DragEvent) => {
    if (!canvasAccepts()) return;
    e.preventDefault();
    if (!isCanvasHovering) setIsCanvasHovering(true);
  };
  const handleCanvasDragLeave = () => {
    if (isCanvasHovering) setIsCanvasHovering(false);
  };
  const handleCanvasDrop = (e: React.DragEvent) => {
    if (!canvasAccepts()) return;
    e.preventDefault();
    setIsCanvasHovering(false);
    if (isAllUnitsDrag()) {
      addAllUnitGroups();
      clearDrag();
      return;
    }
    const unitId = getDraggedUnit();
    if (unitId) addUnitGroup(unitId);
    clearDrag();
  };

  return (
    <main
      onDragOver={handleCanvasDragOver}
      onDragLeave={handleCanvasDragLeave}
      onDrop={handleCanvasDrop}
      className={`flex flex-col gap-3 overflow-y-auto rounded-lg border-2 border-dashed p-3 transition-colors ${
        isCanvasHovering
          ? "border-moss-primary bg-moss-primary/5"
          : "border-neutral-200 bg-white"
      }`}
    >
      {groups.length === 0 ? (
        <div className="text-neutral-secondary-mid flex flex-1 flex-col items-center justify-center gap-2 text-center text-sm">
          <Cube size={36} weight="duotone" className="text-neutral-300" />
          <div className="font-medium">Sleep kostenplaatsen hierheen</div>
          <div className="text-xs">
            Sleep een unit uit de bovenste balk om een kostenplaats toe te
            voegen, sleep daarna labels op de kostenplaats om de werken te
            plannen.
          </div>
        </div>
      ) : (
        <>
          <div className="text-neutral-primary text-sm font-medium">
            {groups.length} kostenplaats{groups.length === 1 ? "" : "en"}
          </div>
          {groups.map((group) => (
            <KostenplaatsGroupCard
              key={group.unitId}
              group={group}
              unit={unitsById.get(group.unitId)}
              bucketTotal={bucketTotal}
              getDraggedLabel={getDraggedLabel}
              clearDrag={clearDrag}
              onAddAllocation={(label) => addAllocation(group.unitId, label)}
              onUpdateAllocation={(allocId, patch) =>
                updateAllocation(group.unitId, allocId, patch)
              }
              onRemoveAllocation={(allocId) =>
                removeAllocation(group.unitId, allocId)
              }
              onRemoveGroup={() => removeGroup(group.unitId)}
            />
          ))}
        </>
      )}
    </main>
  );
};

// ============================================================================
// Group card
// ============================================================================

interface KostenplaatsGroupCardProps {
  group: KostenplaatsGroup;
  unit?: UnitDTO;
  bucketTotal: number;
  getDraggedLabel: () => RenovationLabel | null;
  clearDrag: () => void;
  onAddAllocation: (label: RenovationLabel) => void;
  onUpdateAllocation: (
    allocId: string,
    patch: Partial<KostenplaatsAllocation>,
  ) => void;
  onRemoveAllocation: (allocId: string) => void;
  onRemoveGroup: () => void;
}

const KostenplaatsGroupCard: React.FC<KostenplaatsGroupCardProps> = ({
  group,
  unit,
  bucketTotal,
  getDraggedLabel,
  clearDrag,
  onAddAllocation,
  onUpdateAllocation,
  onRemoveAllocation,
  onRemoveGroup,
}) => {
  const [isDropHovering, setIsDropHovering] = useState(false);
  const surface = getUnitSurface(unit);
  const groupTotal = group.allocations.reduce(
    (s, a) => s + (Number(a.amount) || 0),
    0,
  );
  const sharePct = bucketTotal > 0 ? (groupTotal / bucketTotal) * 100 : 0;

  const handleGroupDragOver = (e: React.DragEvent) => {
    if (!getDraggedLabel()) return;
    e.preventDefault();
    e.stopPropagation();
    if (!isDropHovering) setIsDropHovering(true);
  };
  const handleGroupDragLeave = () => {
    if (isDropHovering) setIsDropHovering(false);
  };
  const handleGroupDrop = (e: React.DragEvent) => {
    const label = getDraggedLabel();
    if (!label) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDropHovering(false);
    onAddAllocation(label);
    clearDrag();
  };

  return (
    <div
      onDragOver={handleGroupDragOver}
      onDragLeave={handleGroupDragLeave}
      onDrop={handleGroupDrop}
      className={`flex flex-col gap-3 rounded-md border p-3 shadow-sm transition-colors ${
        isDropHovering
          ? "border-moss-primary bg-moss-primary/5"
          : "border-neutral-200 bg-white"
      }`}
    >
      {/* Group header: kostenplaats (unit) identity + total + remove */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="bg-moss-primary/5 text-moss-primary border-moss-primary/30 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm font-semibold">
            <Cube size={14} weight="duotone" />
            <span className="tabular-nums">{group.unitId}</span>
            {surface !== null && (
              <span className="text-neutral-secondary-mid border-l border-moss-primary/20 pl-1.5 text-[10px] tabular-nums">
                {surface} m²
              </span>
            )}
          </span>
          <span
            className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
            title="Lijnen uit een strategisch budget zijn altijd strategisch budget"
          >
            <Wallet size={10} weight="fill" />
            Strategisch
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-neutral-primary text-sm font-semibold tabular-nums">
              {formatCurrency(groupTotal)}
            </div>
            <div className="text-neutral-secondary-mid text-[10px] tabular-nums">
              {sharePct.toFixed(1)}% van strategisch budget
            </div>
          </div>
          <button
            type="button"
            onClick={onRemoveGroup}
            className="hover:text-rose-600 rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-rose-50"
            title="Verwijder kostenplaats"
          >
            <Trash size={14} />
          </button>
        </div>
      </div>

      {/* Allocations: one planned budgetlijn (label + bedrag) per row */}
      <div className="rounded-md border border-dashed border-neutral-200 bg-neutral-50/60 p-2">
        <div className="text-neutral-secondary-mid mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide">
          <Tag size={11} weight="duotone" />
          Werken
          <span className="text-neutral-400 normal-case font-normal italic tracking-normal">
            {group.allocations.length === 0
              ? "sleep labels uit het linkerpaneel hierheen"
              : `${group.allocations.length} budgetlijn${
                  group.allocations.length === 1 ? "" : "en"
                }`}
          </span>
        </div>
        {group.allocations.length === 0 ? (
          <div className="text-neutral-secondary-mid flex flex-col items-center gap-1 py-3 text-center text-[11px] italic">
            <Coin size={20} weight="duotone" className="text-neutral-300" />
            Nog geen werken gepland voor deze kostenplaats
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {group.allocations.map((alloc) => (
              <AllocationRow
                key={alloc.allocId}
                alloc={alloc}
                onChange={(patch) => onUpdateAllocation(alloc.allocId, patch)}
                onRemove={() => onRemoveAllocation(alloc.allocId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Allocation row
// ============================================================================

interface AllocationRowProps {
  alloc: KostenplaatsAllocation;
  onChange: (patch: Partial<KostenplaatsAllocation>) => void;
  onRemove: () => void;
}

const AllocationRow: React.FC<AllocationRowProps> = ({
  alloc,
  onChange,
  onRemove,
}) => {
  const tone =
    LABEL_TYPE_TONE[alloc.labelType ?? "algemeen"] ?? LABEL_TYPE_TONE.algemeen;

  return (
    <div className="grid grid-cols-[150px_1fr_140px_auto] items-start gap-3 rounded-md border border-neutral-200 bg-white p-2">
      <span
        className={`inline-flex items-center gap-1 self-start rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
        title={alloc.labelName}
      >
        <Tag size={10} weight="fill" />
        <span className="break-words leading-tight">{alloc.labelName}</span>
      </span>

      <div className="flex flex-col gap-1.5">
        <Input
          value={alloc.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titel van de budgetlijn"
          className="h-8 text-sm"
        />
        <Textarea
          value={alloc.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Korte beschrijving (optioneel)"
          rows={2}
          className="min-h-[44px] resize-y text-xs"
        />
      </div>

      <div className="relative w-full">
        <span className="text-neutral-secondary-mid pointer-events-none absolute inset-y-0 left-2 flex items-center text-xs">
          €
        </span>
        <Input
          type="number"
          min={0}
          step={1000}
          value={alloc.amount || ""}
          onChange={(e) => onChange({ amount: Number(e.target.value || 0) })}
          placeholder="0"
          className="h-8 pl-5 text-right text-sm tabular-nums"
        />
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="hover:text-rose-600 self-start rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-rose-50"
        title="Verwijder budgetlijn"
      >
        <Trash size={14} />
      </button>
    </div>
  );
};

export default KostenplaatsVerdelingCanvas;
